import { z } from "zod";
import { PHOTO_CATEGORIES, VIDEO_MAX_BYTES, VIDEO_MIME_TYPES } from "@fellowflats/rules/constants";
import { MAX_BEDS_PER_ROOM, MAX_ROOMS_PER_LISTING } from "@fellowflats/rules/listing-shape";
import { GenderRuleSchema, ListingTypeSchema, PropertyTypeSchema, RoomTypeSchema } from "./listings";

/**
 * Changes a landlord makes to their own listings: the listing itself, its
 * rooms, amenities, photos and videos, publishing, and asking for the
 * verified badge.
 *
 * Money is integer CENTS, as everywhere on the wire; the website turns typed
 * dollars into cents before it sends anything. Bodies are strict: an unexpected
 * field (a `landlordId`, a `status` on create) is a 400.
 *
 * The listing a change applies to is always one the caller owns. A listing that
 * exists but belongs to someone else answers exactly like one that does not
 * exist (404), so ids cannot be probed.
 */

const cents = z.number().int();

const LocationFields = {
  city: z.string().min(1).max(80),
  province: z.string().max(40),
  country: z.string().max(2).optional(),
  /** A city picked from the list. A stale or unknown id falls back to the typed city. */
  cityId: z.uuid().optional(),
};

export const RoomBodySchema = z.strictObject({
  label: z.string().min(1).max(80),
  capacity: z.number().int().min(1).max(MAX_BEDS_PER_ROOM),
  priceCents: cents,
  roomType: RoomTypeSchema,
  genderRule: GenderRuleSchema,
  furnished: z.boolean().optional(),
});
export type RoomBody = z.infer<typeof RoomBodySchema>;

export const CreatePropertyBodySchema = z.strictObject({
  title: z.string().min(3).max(120),
  description: z.string().max(4000).optional(),
  propertyType: PropertyTypeSchema,
  listingType: ListingTypeSchema,
  /** ENTIRE_UNIT only: the rent for the whole place. The single room is built from it by the API. */
  rentCents: cents.optional(),
  bedrooms: z.number().int().min(0).max(20).optional(),
  bathrooms: z.number().min(0).max(20).optional(),
  furnished: z.boolean().optional(),
  addressLine1: z.string().max(160).optional(),
  ...LocationFields,
  postalCode: z.string().max(12).optional(),
  nearestSchoolId: z.uuid().optional(),
  amenities: z.array(z.string().max(60)).max(30).optional(),
  houseRules: z.array(z.string().max(60)).max(30).optional(),
  /** YYYY-MM-DD or a full ISO instant. */
  availableFrom: z.string().refine((v) => !Number.isNaN(Date.parse(v)), "Invalid date.").optional(),
  /** BY_THE_BED only; ignored for a whole unit. */
  rooms: z.array(RoomBodySchema).max(MAX_ROOMS_PER_LISTING).optional(),
});
export type CreatePropertyBody = z.infer<typeof CreatePropertyBodySchema>;

export const CreatePropertyReplySchema = z.object({
  id: z.uuid(),
  slug: z.string(),
  /** DRAFT until the landlord is verified. */
  status: z.enum(["ACTIVE", "DRAFT"]),
});

export const UpdatePropertyBodySchema = z.strictObject({
  title: z.string().min(3).max(120),
  description: z.string().max(4000).optional(),
  propertyType: PropertyTypeSchema,
  addressLine1: z.string().max(160).optional(),
  ...LocationFields,
  postalCode: z.string().max(12).optional(),
});
export type UpdatePropertyBody = z.infer<typeof UpdatePropertyBodySchema>;

export const PropertyStatusBodySchema = z.strictObject({ status: z.enum(["ACTIVE", "PAUSED", "ARCHIVED"]) });

export const AmenityBodySchema = z.strictObject({
  name: z.string().trim().min(1).max(60),
  isPaid: z.boolean(),
  /** Required (and positive) when paid; ignored when free. */
  priceCents: cents.nullable().optional(),
  billing: z.enum(["MONTHLY", "ONE_TIME"]),
  isOptional: z.boolean(),
});
export type AmenityBody = z.infer<typeof AmenityBodySchema>;
export const AmenitiesBodySchema = z.strictObject({ amenities: z.array(AmenityBodySchema).max(40) });

export const AddImageBodySchema = z.strictObject({
  url: z.url().max(500),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
});
const CATEGORY_VALUES = PHOTO_CATEGORIES.map((c) => c.value) as [string, ...string[]];
export const ImageCategoryBodySchema = z.strictObject({ category: z.enum(CATEGORY_VALUES) });
export const MoveBodySchema = z.strictObject({ direction: z.enum(["up", "down"]) });

export const AddVideoBodySchema = z.strictObject({
  url: z.url().max(500),
  mimeType: z.enum(VIDEO_MIME_TYPES),
  sizeBytes: z.number().int().positive().max(VIDEO_MAX_BYTES),
  durationSec: z.number().int().positive().max(60 * 60).optional(),
});

/** Which listing a change touched, so the website can refresh its pages. */
export const PlacedReplySchema = z.object({ propertyId: z.uuid(), slug: z.string() });
export type Placed = z.infer<typeof PlacedReplySchema>;
/** The same, for a change that created something (a room, a photo, a video). */
export const CreatedReplySchema = PlacedReplySchema.extend({ id: z.uuid() });

/**
 * Why a listing change was refused, as the `code` of the API's error reply.
 * Some carry `details` the website puts into its translated message.
 */
export const ListingErrorCodes = [
  "invalid_input", // the body did not match this contract; the website's own form checks should catch it first
  "not_found",
  "rate_limited",
  "listing_limit", // details.limit
  "rent_required",
  "rooms_required",
  "too_many_rooms",
  "invalid_rent",
  "invalid_room_prices",
  "not_verified", // publishing before the landlord is verified
  "overdue_requests", // republishing after an SLA pause with requests still unanswered
  "invalid_price",
  "room_limit",
  "room_not_found",
  "capacity_below_filled", // details.count
  "room_in_use",
  "amenity_twice", // details.name
  "amenity_unlawful", // details.reason (packages/rules tenancy-rules)
  "amenity_needs_price", // details.name
  "amenity_in_booking", // details.name
  "url_not_ours",
  "photo_limit", // details.max
  "video_limit", // details.max
  "already_verified",
  "verification_open",
] as const;
export type ListingErrorCode = (typeof ListingErrorCodes)[number];
